import { useId, useState } from 'react';
import { Link } from 'react-router';
import { Box, Button, Container, Menu, MenuItem, Stack, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { CaretDownIcon } from '@phosphor-icons/react';

import { appConfig } from '@/config/app';
import { buildPageTree, type PageTreeNode } from '@/lib/page-tree';
import { getUnitTypeLabel } from '@/lib/seed-content';
import { BlockRenderer } from '@/components/blocks/BlockRenderer';
import type { Block, Page, UnitType } from '@/types/database';

// Standard clip-based visually-hidden technique (content stays in the
// accessibility tree and reachable by screen readers, removed only from
// visual layout) — same values as @mui/utils' visuallyHidden helper,
// inlined here rather than adding that package as a direct dependency for
// one style object.
const visuallyHiddenSx: SxProps<Theme> = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
};

export interface SitePageChromeUnit {
  name: string;
  unitType: UnitType | null;
  unitIdentifier: string | null;
  location: string | null;
}

interface SitePageChromeProps {
  basePath: string;
  blocks: Block[];
  currentPage: Page | null;
  // Pages eligible for nav consideration — the caller decides what
  // "eligible" means (published-only for the real public site, every draft
  // page for the Studio preview) before this component groups them by
  // buildPageTree's show_in_nav / one-level-of-nesting rules.
  pages: Page[];
  logoUrl: string | null;
  unit: SitePageChromeUnit;
}

// The public site's full header/nav/footer chrome, shared by the real
// /site/:slug route and the authenticated /studio/preview route so a
// WYSIWYG preview is the exact same render path a visitor gets, not a
// lookalike that can drift out of sync.
export function SitePageChrome({ basePath, blocks, currentPage, pages, logoUrl, unit }: SitePageChromeProps) {
  const { navTree } = buildPageTree(pages);

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Box component="header" sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Container maxWidth="lg" sx={{ py: 1.5 }}>
          <Stack direction="row" spacing={2} sx={{ alignItems: "center", justifyContent: "space-between" }}>
            <Button component={Link} to={basePath || '/'} variant="text" sx={{ textTransform: 'none', px: 0 }}>
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {logoUrl ? (
                  <Box component="img" src={logoUrl} alt="" sx={{ height: 32, width: 32, borderRadius: 0.75, objectFit: 'contain' }} />
                ) : null}
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{unit.name}</Typography>
              </Stack>
            </Button>
            <Stack component="nav" aria-label="Primary" direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' } }}>
              {navTree.map((node) =>
                node.children.length > 0 ? (
                  <NavDropdown key={node.page.id} node={node} basePath={basePath} currentPage={currentPage} />
                ) : (
                  <Button
                    key={node.page.id}
                    component={Link}
                    to={`${basePath}/${node.page.slug}`}
                    variant={currentPage?.id === node.page.id ? 'contained' : 'text'}
                    size="small"
                  >
                    {node.page.title}
                  </Button>
                )
              )}
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Box component="main">
        {/* Visually hidden — gives screen-reader users a real, single h1
            landmark per page (there's no visible page-title heading in this
            chrome) without duplicating whatever visible heading a Hero/Text
            block at the top of the page already shows, and makes the h2+
            headings blocks render (see e.g. HeroBlockView) valid, non-
            skipped subheadings under it. */}
        <Typography component="h1" sx={visuallyHiddenSx}>
          {currentPage?.title ?? unit.name}
        </Typography>
        {blocks.map((block) => (
          <BlockRenderer key={block.id} block={block} basePath={basePath} />
        ))}
      </Box>

      <Box component="footer" sx={{ borderTop: '1px solid', borderColor: 'divider', py: 4, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Stack spacing={0.75} sx={{ alignItems: "center", textAlign: "center" }}>
            <Typography sx={{ fontWeight: 700 }}>{unit.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              {unit.unitType ? getUnitTypeLabel(unit.unitType) : null} {unit.unitIdentifier}
              {unit.location ? ` - ${unit.location}` : ''}
            </Typography>
            <Typography variant="caption" color="text.secondary">Powered by {appConfig.name}</Typography>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
}

function NavDropdown({ node, basePath, currentPage }: { node: PageTreeNode; basePath: string; currentPage: Page | null }) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isActive = node.page.id === currentPage?.id || node.children.some((child) => child.id === currentPage?.id);
  const menuId = useId();
  const open = Boolean(anchorEl);

  return (
    <>
      <Button
        variant={isActive ? 'contained' : 'text'}
        size="small"
        endIcon={<CaretDownIcon size={14} />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        aria-haspopup="true"
        aria-controls={open ? menuId : undefined}
        aria-expanded={open ? 'true' : undefined}
      >
        {node.page.title}
      </Button>
      <Menu id={menuId} anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <MenuItem component={Link} to={`${basePath}/${node.page.slug}`} onClick={() => setAnchorEl(null)}>
          {node.page.title}
        </MenuItem>
        {node.children.map((child) => (
          <MenuItem key={child.id} component={Link} to={`${basePath}/${child.slug}`} onClick={() => setAnchorEl(null)}>
            {child.title}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
